import React, { useState } from 'react';
import { FileText, Plus, Check, Sparkles, ChevronRight, Clock, FileStack, ListTodo } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';

const CaseOverviewNew = ({ 
  caseId, 
  caseData, 
  timeEntries = [], 
  tasks = [], 
  documents = [],
  onRegisterTime,
  onNewTask,
  onViewAllTasks,
  onViewAllDocuments
}: { 
  caseId?: string; 
  caseData: any; 
  timeEntries?: any[]; 
  tasks?: any[]; 
  documents?: any[];
  activities?: any[];
  onRegisterTime?: () => void;
  onNewTask?: () => void;
  onViewAllTasks?: () => void;
  onViewAllDocuments?: () => void;
}) => {
  const [hoveredTask, setHoveredTask] = useState<string | null>(null);
  const [hoveredDoc, setHoveredDoc] = useState<string | null>(null);
  const [aiHovered, setAiHovered] = useState(false);
  const { language } = useTranslation();
  
  const en = language === 'en';
  
  if (!caseData) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: '16px', height: '16px', border: '2px solid #e5e5e5', borderTopColor: '#000', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
      </div>
    );
  }

  const stats = {
    hours: timeEntries.reduce((sum, e) => sum + (e.hours || 0), 0),
    openTasks: tasks.filter(t => !t.completed).length,
    totalTasks: tasks.length,
    docs: documents.length,
    unbilled: caseData.unbilled_amount || 0,
  };

  const pendingTasks = tasks.filter(t => !t.completed).slice(0, 4);
  const recentDocs = documents.slice(0, 4);
  
  const formatDate = (d: string | undefined) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString(en ? 'en-US' : 'nb-NO', { month: 'short', day: 'numeric' });
  };

  const isOverdue = caseData.deadline && new Date(caseData.deadline) < new Date();
  const description = caseData.description || caseData.intake_summary || '';

  return (
    <div data-testid="case-overview-worldclass" style={{ 
      minHeight: '100%', 
      background: '#fafafa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Inter", system-ui, sans-serif',
    }}>
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '48px 40px 120px' }}>
        
        {/* HEADER SECTION */}
        <header style={{ marginBottom: '40px' }}>
          {/* Client breadcrumb */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginBottom: '20px'
          }}>
            <span style={{ 
              fontSize: '12px', 
              color: '#6b7280', 
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              {caseData.client_name}
            </span>
            
            {/* Action Buttons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button 
                onClick={onNewTask}
                data-testid="new-task-btn"
                style={{
                  height: '34px',
                  padding: '0 14px',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: '#374151',
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.15s ease',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f9fafb';
                  e.currentTarget.style.borderColor = '#d1d5db';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#fff';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
              >
                <Plus size={14} strokeWidth={2.5} />
                {en ? 'New task' : 'Ny oppgave'}
              </button>
              <button 
                onClick={onRegisterTime}
                data-testid="log-time-btn"
                style={{
                  height: '34px',
                  padding: '0 16px',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: '#fff',
                  background: '#111827',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#1f2937';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#111827';
                }}
              >
                {en ? 'Log time' : 'Registrer tid'}
              </button>
            </div>
          </div>
          
          {/* Title */}
          <h1 style={{ 
            fontSize: '28px', 
            fontWeight: 600, 
            color: '#111827', 
            letterSpacing: '-0.02em',
            lineHeight: 1.3,
            margin: '0 0 12px 0',
          }}>
            {caseData.topic || 'Untitled'}
          </h1>
          
          {/* Status badges */}
          {isOverdue && (
            <span style={{
              display: 'inline-block',
              fontSize: '11px',
              fontWeight: 600,
              color: '#dc2626',
              background: '#fef2f2',
              padding: '4px 10px',
              borderRadius: '6px',
              marginBottom: '12px',
            }}>
              {en ? 'OVERDUE' : 'FORFALT'}
            </span>
          )}
          
          {/* Description */}
          {description && (
            <p style={{ 
              fontSize: '14px', 
              color: '#6b7280', 
              lineHeight: 1.7,
              margin: 0,
              maxWidth: '600px',
            }}>
              {description.length > 180 ? description.slice(0, 180) + '...' : description}
            </p>
          )}
        </header>

        {/* STATS CARDS */}
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '16px',
          marginBottom: '32px',
        }}>
          {/* Hours */}
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid #f3f4f6',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Clock size={14} color="#9ca3af" />
              <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {en ? 'Hours' : 'Timer'}
              </span>
            </div>
            <div style={{ 
              fontSize: '28px', 
              fontWeight: 600, 
              color: '#111827',
              fontFeatureSettings: '"tnum"',
            }}>
              {stats.hours.toFixed(1)}
            </div>
          </div>
          
          {/* Unbilled */}
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid #f3f4f6',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {en ? 'Unbilled' : 'Ufakturert'}
              </span>
            </div>
            <div style={{ 
              fontSize: '28px', 
              fontWeight: 600, 
              color: '#111827',
              fontFeatureSettings: '"tnum"',
            }}>
              {stats.unbilled > 0 ? new Intl.NumberFormat('nb-NO').format(stats.unbilled) : '0'}
              <span style={{ fontSize: '14px', fontWeight: 500, color: '#9ca3af', marginLeft: '4px' }}>kr</span>
            </div>
          </div>
          
          {/* Tasks */}
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid #f3f4f6',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <ListTodo size={14} color="#9ca3af" />
              <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {en ? 'Tasks' : 'Oppgaver'}
              </span>
            </div>
            <div style={{ 
              fontSize: '28px', 
              fontWeight: 600, 
              color: '#111827',
              fontFeatureSettings: '"tnum"',
            }}>
              {stats.openTasks}
              <span style={{ fontSize: '14px', fontWeight: 500, color: '#d1d5db' }}>/{stats.totalTasks}</span>
            </div>
          </div>
          
          {/* Documents */}
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid #f3f4f6',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <FileStack size={14} color="#9ca3af" />
              <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {en ? 'Docs' : 'Dok'}
              </span>
            </div>
            <div style={{ 
              fontSize: '28px', 
              fontWeight: 600, 
              color: '#111827',
              fontFeatureSettings: '"tnum"',
            }}>
              {stats.docs}
            </div>
          </div>
        </div>

        {/* AI ASSISTANT BOX - World Class Dark Indigo */}
        <div 
          data-testid="ai-assistant-box"
          onMouseEnter={() => setAiHovered(true)}
          onMouseLeave={() => setAiHovered(false)}
          style={{
            marginBottom: '40px',
            padding: '24px 28px',
            borderRadius: '20px',
            cursor: 'pointer',
            background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #1e1b4b 100%)',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: aiHovered 
              ? '0 25px 50px -12px rgba(30, 27, 75, 0.5), 0 0 0 1px rgba(139, 92, 246, 0.2)' 
              : '0 10px 40px -10px rgba(30, 27, 75, 0.4)',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            transform: aiHovered ? 'translateY(-3px) scale(1.005)' : 'none',
          }}
        >
          {/* Subtle gradient overlay for depth */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(ellipse at top right, rgba(139, 92, 246, 0.15) 0%, transparent 50%)',
            pointerEvents: 'none',
          }} />
          
          {/* Animated glow effect on hover */}
          <div style={{
            position: 'absolute',
            top: '-50%',
            left: '-50%',
            width: '200%',
            height: '200%',
            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 40%)',
            opacity: aiHovered ? 1 : 0,
            transition: 'opacity 0.4s ease',
            pointerEvents: 'none',
          }} />
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '18px', position: 'relative', zIndex: 1 }}>
            {/* Icon container with subtle glow */}
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: aiHovered 
                ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.3) 0%, rgba(99, 102, 241, 0.2) 100%)'
                : 'rgba(255, 255, 255, 0.08)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(139, 92, 246, 0.2)',
              transition: 'all 0.3s ease',
              boxShadow: aiHovered ? '0 0 20px rgba(139, 92, 246, 0.3)' : 'none',
            }}>
              <Sparkles 
                size={22} 
                color="#a78bfa" 
                style={{
                  filter: aiHovered ? 'drop-shadow(0 0 8px rgba(167, 139, 250, 0.6))' : 'none',
                  transition: 'filter 0.3s ease',
                }}
              />
            </div>
            
            <div style={{ flex: 1 }}>
              <div style={{ 
                fontSize: '16px', 
                fontWeight: 600, 
                color: '#fff',
                marginBottom: '4px',
                letterSpacing: '-0.01em',
              }}>
                {en ? 'Ask Lexy AI' : 'Spør Lexy AI'}
              </div>
              <div style={{ 
                fontSize: '13px', 
                color: 'rgba(255, 255, 255, 0.6)',
                letterSpacing: '0.01em',
              }}>
                {en ? 'Get insights, summaries, and drafts instantly' : 'Få innsikt, oppsummeringer og utkast umiddelbart'}
              </div>
            </div>
            
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: aiHovered ? 'rgba(139, 92, 246, 0.2)' : 'rgba(255, 255, 255, 0.05)',
              transition: 'all 0.3s ease',
            }}>
              <ChevronRight 
                size={18} 
                style={{ 
                  color: aiHovered ? '#a78bfa' : 'rgba(255, 255, 255, 0.4)',
                  transition: 'all 0.3s ease',
                  transform: aiHovered ? 'translateX(2px)' : 'none',
                }}
              />
            </div>
          </div>
        </div>

        {/* CONTENT GRID */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          
          {/* Tasks Section */}
          <section style={{
            background: '#fff',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid #f3f4f6',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h2 style={{ 
                fontSize: '13px', 
                fontWeight: 600, 
                color: '#111827', 
                margin: 0,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}>
                {en ? 'Tasks' : 'Oppgaver'}
              </h2>
              {tasks.length > 0 && (
                <button 
                  onClick={onViewAllTasks}
                  data-testid="view-all-tasks"
                  style={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#7c3aed',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    transition: 'opacity 0.15s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                >
                  {en ? 'See all' : 'Se alle'}
                </button>
              )}
            </div>
            
            {pendingTasks.length > 0 ? (
              <div>
                {pendingTasks.map((task: any, idx: number) => (
                  <div 
                    key={task.id}
                    data-testid={`task-item-${task.id}`}
                    onMouseEnter={() => setHoveredTask(task.id)}
                    onMouseLeave={() => setHoveredTask(null)}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '14px',
                      padding: '14px 0',
                      cursor: 'pointer',
                      borderTop: idx === 0 ? 'none' : '1px solid #f3f4f6',
                    }}
                  >
                    <div style={{
                      width: '20px',
                      height: '20px',
                      marginTop: '2px',
                      borderRadius: '6px',
                      border: `2px solid ${hoveredTask === task.id ? '#7c3aed' : '#e5e7eb'}`,
                      background: hoveredTask === task.id ? '#7c3aed' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.15s',
                      flexShrink: 0,
                    }}>
                      {hoveredTask === task.id && <Check size={12} color="#fff" strokeWidth={3} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ 
                        fontSize: '14px', 
                        color: '#374151',
                        lineHeight: 1.5,
                        fontWeight: 450,
                      }}>
                        {task.title}
                      </div>
                      {task.due_date && (
                        <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                          {formatDate(task.due_date)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ 
                padding: '32px 0', 
                textAlign: 'center',
                color: '#9ca3af',
              }}>
                <ListTodo size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
                <p style={{ fontSize: '13px', margin: 0 }}>
                  {en ? 'No tasks yet' : 'Ingen oppgaver ennå'}
                </p>
              </div>
            )}
          </section>

          {/* Documents Section */}
          <section style={{
            background: '#fff',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid #f3f4f6',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h2 style={{ 
                fontSize: '13px', 
                fontWeight: 600, 
                color: '#111827', 
                margin: 0,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}>
                {en ? 'Documents' : 'Dokumenter'}
              </h2>
              {documents.length > 0 && (
                <button 
                  onClick={onViewAllDocuments}
                  data-testid="view-all-docs"
                  style={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#7c3aed',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    transition: 'opacity 0.15s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                >
                  {en ? 'See all' : 'Se alle'}
                </button>
              )}
            </div>
            
            {recentDocs.length > 0 ? (
              <div>
                {recentDocs.map((doc: any, idx: number) => (
                  <div 
                    key={doc.id}
                    data-testid={`doc-item-${doc.id}`}
                    onMouseEnter={() => setHoveredDoc(doc.id)}
                    onMouseLeave={() => setHoveredDoc(null)}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '14px',
                      padding: '14px 0',
                      cursor: 'pointer',
                      borderTop: idx === 0 ? 'none' : '1px solid #f3f4f6',
                    }}
                  >
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '10px',
                      background: hoveredDoc === doc.id ? '#f3f4f6' : '#fafafa',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.15s',
                      flexShrink: 0,
                    }}>
                      <FileText size={16} color={hoveredDoc === doc.id ? '#374151' : '#9ca3af'} strokeWidth={1.5} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ 
                        fontSize: '14px', 
                        color: '#374151',
                        lineHeight: 1.4,
                        fontWeight: 450,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {doc.original_filename || doc.filename || 'Document'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                        {formatDate(doc.uploaded_at || doc.created_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ 
                padding: '32px 0', 
                textAlign: 'center',
                color: '#9ca3af',
              }}>
                <FileStack size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
                <p style={{ fontSize: '13px', margin: 0 }}>
                  {en ? 'No documents yet' : 'Ingen dokumenter ennå'}
                </p>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default CaseOverviewNew;
